import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useSession } from "../data/usePage.js";
import { useRepo } from "../data/repo.js";
import { fetchExportKey, createExportKey, revokeExportKey, type ExportKey } from "../api.js";
import { EDIT_SLOT_ID } from "../layout/RightSidebar.js";

export function ExportUrlCard({ path }: { path: string }) {
  const session = useSession();
  const repo = useRepo();
  const [key, setKey] = useState<ExportKey | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (session.status !== "ready" || !session.data.user) return;
    setKey(undefined);
    fetchExportKey(repo, path)
      .then(setKey)
      .catch(() => setKey(null));
  }, [repo, path, session.status]);

  const create = async () => {
    setBusy(true);
    try {
      setKey(await createExportKey(repo, path));
    } catch {
      /* ignore */
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!key) return;
    setBusy(true);
    try {
      await revokeExportKey(repo, key.token);
      setKey(null);
    } catch {
      /* ignore */
    } finally {
      setBusy(false);
    }
  };

  const copy = () => {
    if (!key) return;
    navigator.clipboard.writeText(key.url);
    setCopied(true);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 2000);
  };

  if (session.status !== "ready" || !session.data.user) return null;

  const slot =
    typeof document !== "undefined" ? document.getElementById(EDIT_SLOT_ID) : null;
  if (!slot) return null;

  return createPortal(
    <section className="dw-card">
      <header className="dw-card-head">CSV export</header>
      <div className="dw-card-body">
        {key === undefined && <span className="dw-muted">…</span>}
        {key === null && (
          <button onClick={create} disabled={busy}>
            {busy ? "Creating…" : "Create export URL"}
          </button>
        )}
        {key && (
          <>
            <input
              className="dw-export-url"
              readOnly
              value={key.url}
              onFocus={(e) => e.currentTarget.select()}
            />
            <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.25rem" }}>
              <button onClick={copy} disabled={busy}>
                {copied ? "Copied!" : "Copy"}
              </button>
              <button onClick={revoke} disabled={busy} className="dw-link dw-danger-link">
                Revoke
              </button>
            </div>
          </>
        )}
      </div>
    </section>,
    slot,
  );
}
